import {createMediaUrl} from '@smart/aem-utils';
import {getLanguage} from '@smart/locale-service';
import type {SelectItemProps} from '@smart/react-components';
import {Button, Markdown, Notification, Text} from '@smart/react-components';
import {DynamicMediaPicture} from '@smart/web-components';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {removeOptionalKeysSuffix} from '../../../../utils/removeOptionalKeysSuffix';
import {transformMarkdown} from '../../../../utils/transform-markdown';
import type {ImageRef} from '../../../graphql';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {LangCode, ViewsFetchingToPostForm} from '../../common/types';
import {AppModelContext} from '../../components/app';
import {ColourSelect} from '../../components/colour-select';
import Fields from '../../components/easy-form/fields';
import type {WrapperHandler} from '../../components/easy-form/types';
import type {PopupContainerMethods} from '../../components/popup-container';
import {PopupContainer} from '../../components/popup-container';
import {useAPI} from '../../hooks/use-api';
import {useFeatureAppEnvHook} from '../../hooks/use-feature-app-env';
import {useLocale} from '../../hooks/use-locale';
import {useSchemas} from '../../hooks/user-schemas';
import {useAnalytics} from '../../utils/analytics';
import localStyles from './styles.module.scss';
type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'accent'
  | 'brabus'
  | 'inverted'
  | 'glass'
  | undefined;

export interface NewsletterPopupState {
  firstName: string;
  lastName: string;
  email: string;
  noPreference: boolean;
  colorInterest?: string;
  lineInterests?: string;
  preferredLang: LangCode;
}

const viewName: ViewsFetchingToPostForm = `newsletter-form-popup`;
// eslint-disable-next-line complexity
export default function NewsletterFormPopup(): JSX.Element {
  const [hasErrors, setHasErrors] = useState(true);
  const [formData, setFormData] = useState<NewsletterPopupState>();
  const formWrapperRef = useRef<HTMLDivElement>(null);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const {languageTag} = useLocale();
  const language = getLanguage(languageTag);
  const [loading, setLoading] = useState(false);
  const [showNoPreference, setShowNoPreference] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>();
  const apiFetch = useAPI(viewName, true);
  const {featureServices} = useFeatureAppEnvHook();

  const popupContainerRef = useRef<PopupContainerMethods | null>(null);
  const {'endpoint-directory': endpointDirectory} = featureServices;
  const {envName, preview} = endpointDirectory;

  const appModel = React.useContext(AppModelContext);
  const {campaignId} = appModel;
  const trackEvent = useAnalytics(campaignId);

  const generalSchemas = useSchemas();

  const schemas = useMemo(() => {
    const {email, firstAndLastNames} = generalSchemas;
    return {
      firstName: firstAndLastNames(true),
      lastName: firstAndLastNames(true),
      email: email(true),
    };
  }, [generalSchemas]);

  const onClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    popupContainerRef.current?.close();
    setTimeout(() => {
      // Reset form state after submission
      setSubmitted(false);
      setSubmitAttempt(false);
      setHasErrors(true);
    }, 300);
  };

  const cmsNesletterPopupTranslations =
    appModel.cms.formsFeatureAppNewsletterPopupUi || {};
  const apiTranslations = appModel.cms.apiErrorMessageKeys || {};

  const campaigns =
    campaignId && campaignId in cmsNesletterPopupTranslations.campaigns
      ? cmsNesletterPopupTranslations.campaigns[campaignId]
      : cmsNesletterPopupTranslations.campaigns[`default`];
  const {lines} = campaigns;

  const imageUrl = cmsNesletterPopupTranslations?.image
    ? createMediaUrl(
        `image`,
        (cmsNesletterPopupTranslations.image as ImageRef)._path ?? ``,
        {envName, preview},
      )
    : ``;

  const changeHandler: WrapperHandler = (state, errors) => {
    setHasErrors(!!errors);
    if ((!!state.colorInterest || !!state.lineInterests) && !showNoPreference)
      setShowNoPreference(true);
    setFormData(state as unknown as NewsletterPopupState);
  };

  const lineOptions = useMemo<SelectItemProps[]>(() => {
    return (lines ?? [])?.map(
      (line: {title?: string; subLine?: string; name?: string}) => ({
        label: `${line.title || ``}: ${line.subLine || ``}`,
        value: line.title || ``,
      }),
    );
  }, [lines]);

  const getThankYouPage = React.useMemo(
    () => (
      <div className={localStyles.thankYouWrapper}>
        <div className={localStyles[`thankYouWrapper-desciption`]}>
          <Text variant="hl-50">{campaigns?.thankYouDescription}</Text>
        </div>
        <div className={localStyles[`thankYouWrapper-content`]}>
          <Text
            className={localStyles[`thankYouWrapper-title`]}
            variant={`hl-200`}
          >
            {campaigns.thankYouTitle}
          </Text>
          <Text variant={`p-200`}>{campaigns.thankYouSubTitle}</Text>
          <div className={localStyles[`thankYouWrapper-close-button`]}>
            <Button variant="primary" mode={300} onClick={onClose}>
              {campaigns.closeText || `Close`}
            </Button>
          </div>
        </div>
      </div>
    ),
    [submitted],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (hasErrors) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }
    if (submitted) {
      setSubmitted(false);
      return;
    }
    if (!formData) return;

    setLoading(true);
    setServerError(undefined);
    const {noPreference = false} = formData;
    try {
      await apiFetch(
        removeOptionalKeysSuffix(
          {
            ...formData,
            lineInterests: noPreference
              ? undefined
              : formData.lineInterests || undefined,
            colorInterest: noPreference
              ? undefined
              : formData.colorInterest || undefined,
          },
          `-popup`,
        ) as NewsletterPopupState,
      );
      trackEvent(`success`, `newsletter-form-signUp`, formData);
      setSubmitted(true);
    } catch (error) {
      setServerError(error as string);
      scrollToRef(formWrapperRef);
      trackEvent(`error`, `newsletter-form-signUp`, formData);
    } finally {
      setLoading(false);
    }
  };

  const getApiErrorMessage = useCallback(() => {
    let apiErrorMessage = ``;
    const getStringFromAEM = apiTranslations.errorMessageKeys?.find(
      (e) => e?.includes(serverError as any),
    );

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      const [_, message] = getStringFromAEM.split(`:`);
      apiErrorMessage = message || ``;
    }
    return apiErrorMessage;
  }, [apiTranslations, serverError]);

  return (
    <PopupContainer
      topic="show-newsletter-popup"
      subscribeFunc={featureServices[`pub-sub`].subscribe}
      ref={(el: PopupContainerMethods | null) => {
        if (el) popupContainerRef.current = el;
      }}
    >
      {!submitted ? (
        <div className={localStyles.container} id="newsletter-form-popup">
          <div className={localStyles.formWrapper} ref={formWrapperRef}>
            <div className={localStyles.titleBar}>
              <Text as="h1" variant="hl-200">
                {campaigns.title}
              </Text>
              <Text as="h1" variant="p-200">
                {campaigns.subTitle}
              </Text>
              {serverError && (
                <Notification
                  signal="error"
                  className={FormStyles.serverError}
                  onOpenChange={(open) => {
                    if (!open) {
                      setServerError(undefined);
                    }
                  }}
                >
                  <Notification.Text>{getApiErrorMessage()}</Notification.Text>
                </Notification>
              )}
            </div>
            <Fields.Wrapper
              translationPrefix={viewName}
              onUpdate={changeHandler}
              showErrors={submitAttempt}
              disabled={loading}
            >
              <form className={localStyles.form} onSubmit={handleSubmit}>
                <fieldset>
                  <div className={localStyles.displayNone}>
                    <Fields.Text tag="campaignId" initial={campaignId} />
                    <Fields.Text tag="preferredLang" initial={language} />
                    <Fields.Text
                      tag="productInterest"
                      initial={campaigns[`productInterest`]}
                    />
                  </div>
                  <div className={localStyles.nameWrap}>
                    <Fields.Text
                      tag={`firstName`}
                      required
                      schema={schemas.firstName}
                      label={cmsNesletterPopupTranslations.firstNameLabel}
                    />
                    <Fields.Text
                      tag={`lastName`}
                      required
                      schema={schemas.lastName}
                      label={cmsNesletterPopupTranslations.lastNameLabel}
                    />
                  </div>
                  <Fields.Text
                    tag={`email`}
                    required
                    schema={schemas.email}
                    label={cmsNesletterPopupTranslations.emailLabel}
                  />

                  <div className={localStyles.productGroup}>
                    <div className={localStyles.imageContainer}>
                      <DynamicMediaPicture
                        imageUrl={imageUrl || ``}
                        imageConfigs={[
                          {
                            aspectRatio: `4-3`,
                            media: `(max-width:768px)`,
                            sizes: `100vw`,
                          },
                          {
                            aspectRatio: `1-1`,
                            media: `(min-width:768px)`,
                            sizes: `100vw`,
                          },
                        ]}
                        imageAltText=""
                        hasImageTransparency={false}
                        initialIsInView
                        noLoadingLogo
                        isObjectFit
                        fetchPriority="high"
                      />
                      <Text
                        className={localStyles[`textInImage`]}
                        variant="p-300"
                      >
                        {campaigns[`imageText`] ?? ``}
                      </Text>
                    </div>
                    <div className={localStyles.selectionGroup}>
                      <Text variant="p-200">
                        {campaigns[`contentText`] ?? ``}
                      </Text>
                      {campaigns[`enabledSelectLine`] && campaigns[`lines`] && (
                        <Fields.Select
                          lightTheme
                          allowEmptySelection
                          style={{maxWidth: `370px`}}
                          tag={`lineInterests`}
                          label={
                            cmsNesletterPopupTranslations.selectLineLabel ?? ``
                          }
                          options={lineOptions}
                        />
                      )}
                      {campaigns[`enabledSelectColour`] && (
                        <ColourSelect
                          tag={`colorInterest`}
                          label={
                            cmsNesletterPopupTranslations.selectColourLabel ??
                            ``
                          }
                        />
                      )}
                      {campaigns[`enabledPreference`] && showNoPreference && (
                        <Fields.Checkbox
                          tag={`noPreference`}
                          label={cmsNesletterPopupTranslations.preferenceText}
                        />
                      )}
                    </div>
                  </div>
                </fieldset>

                <Text as="p" variant="lgl-100">
                  <Markdown>
                    {transformMarkdown(
                      cmsNesletterPopupTranslations.legalConsent?.markdown ||
                        ``,
                    )}
                  </Markdown>
                </Text>
                <Button
                  type="submit"
                  mode={300}
                  variant={
                    (cmsNesletterPopupTranslations.submitButtonType as ButtonVariants) ||
                    `accent`
                  }
                  className={localStyles.button}
                  loading={loading}
                  disabled={(submitAttempt && hasErrors) || loading}
                >
                  {cmsNesletterPopupTranslations.submitText || `Submit`}
                  <Button.Spinner />
                </Button>
              </form>
            </Fields.Wrapper>
          </div>
        </div>
      ) : (
        getThankYouPage
      )}
    </PopupContainer>
  );
}
